/**
 * Adds a GPS timestamp watermark to a captured photo.
 * Stamps location name, GPS coordinates, date/time, and WTF branding
 * onto the bottom of the image — similar to GPS Map Camera apps.
 *
 * @param {string} imageSrc - Base64 image from webcam
 * @param {object} options - Watermark data
 * @param {string} options.locationName - Gym name or "Sector 9, Noida"
 * @param {string} options.locationType - "gym" or "hq"
 * @param {number|null} options.latitude - GPS latitude
 * @param {number|null} options.longitude - GPS longitude
 * @param {number|null} options.accuracy - GPS accuracy in meters
 * @param {string} options.type - "check-in" or "check-out"
 * @returns {Promise<string>} - Watermarked base64 image
 */
export async function addGpsWatermark(imageSrc, options = {}) {
  const {
    locationName = 'Unknown Location',
    locationType = 'gym',
    latitude = null,
    longitude = null,
    accuracy = null,
    type = 'check-in',
  } = options;

  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      canvas.width = img.width;
      canvas.height = img.height;

      // Draw original image
      ctx.drawImage(img, 0, 0);

      // --- Watermark overlay at bottom ---
      const barHeight = Math.max(100, img.height * 0.14);
      const barY = img.height - barHeight;

      // Semi-transparent dark gradient background
      const gradient = ctx.createLinearGradient(0, barY - 20, 0, img.height);
      gradient.addColorStop(0, 'rgba(0, 0, 0, 0)');
      gradient.addColorStop(0.15, 'rgba(0, 0, 0, 0.75)');
      gradient.addColorStop(1, 'rgba(0, 0, 0, 0.9)');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, barY - 20, img.width, barHeight + 20);

      // Text settings
      const padding = img.width * 0.04;
      const baseFontSize = Math.max(12, Math.min(18, img.width * 0.028));
      let textY = barY + padding * 0.6;

      // --- Line 1: Location name (large, bold) ---
      ctx.fillStyle = '#FFFFFF';
      ctx.font = `bold ${baseFontSize * 1.4}px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;
      ctx.textBaseline = 'top';

      // Location pin icon (unicode) + name
      const locationIcon = locationType === 'hq' ? '🏢' : '📍';
      ctx.fillText(`${locationIcon} ${locationName}`, padding, textY);
      textY += baseFontSize * 1.8;

      // --- Line 2: GPS coordinates ---
      ctx.fillStyle = 'rgba(255, 255, 255, 0.85)';
      ctx.font = `${baseFontSize * 0.9}px "SF Mono", "Fira Code", monospace, sans-serif`;

      if (latitude != null && longitude != null) {
        const coordText = `${latitude.toFixed(6)}°N, ${longitude.toFixed(6)}°E`;
        const accText = accuracy ? ` (±${Math.round(accuracy)}m)` : '';
        ctx.fillText(`📡 ${coordText}${accText}`, padding, textY);
      } else {
        ctx.fillText('📡 GPS: Manual confirmation', padding, textY);
      }
      textY += baseFontSize * 1.5;

      // --- Line 3: Date and time (IST) ---
      const now = new Date();
      const istOptions = { timeZone: 'Asia/Kolkata' };
      const dateStr = now.toLocaleDateString('en-IN', {
        ...istOptions,
        weekday: 'short',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
      const timeStr = now.toLocaleTimeString('en-IN', {
        ...istOptions,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
      });
      const typeLabel = type === 'check-out' ? 'CHECK-OUT' : 'CHECK-IN';

      ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
      ctx.font = `${baseFontSize * 0.85}px -apple-system, BlinkMacSystemFont, sans-serif`;
      ctx.fillText(`🕐 ${dateStr}  ${timeStr}  IST`, padding, textY);
      textY += baseFontSize * 1.5;

      // --- Line 4: WTF branding + type badge ---
      // Type badge (right-aligned)
      const badgeText = typeLabel;
      ctx.font = `bold ${baseFontSize * 0.8}px -apple-system, BlinkMacSystemFont, sans-serif`;
      const badgeWidth = ctx.measureText(badgeText).width + 16;
      const badgeX = img.width - padding - badgeWidth;
      const badgeY = barY + padding * 0.4;

      // Badge background
      ctx.fillStyle = type === 'check-out' ? 'rgba(239, 68, 68, 0.9)' : 'rgba(124, 29, 62, 0.9)';
      ctx.beginPath();
      ctx.roundRect(badgeX, badgeY, badgeWidth, baseFontSize * 1.3, 4);
      ctx.fill();

      // Badge text
      ctx.fillStyle = '#FFFFFF';
      ctx.font = `bold ${baseFontSize * 0.8}px -apple-system, BlinkMacSystemFont, sans-serif`;
      ctx.fillText(badgeText, badgeX + 8, badgeY + 3);

      // WTF branding (bottom left)
      ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
      ctx.font = `bold ${baseFontSize * 0.7}px -apple-system, BlinkMacSystemFont, sans-serif`;
      ctx.fillText('WTF GYMS ATTENDANCE', padding, textY);

      resolve(canvas.toDataURL('image/jpeg', 0.85));
    };

    img.onerror = () => {
      // If watermarking fails, return original image
      resolve(imageSrc);
    };

    img.src = imageSrc;
  });
}
